import {
    ChevronRight,
    ClipboardCheck,
    ExternalLink,
    Layers,
    List,
    LogOut,
    Play,
    Upload,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@shared/ui/Button";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { PaneFooter } from "@features/pane-manager/PaneFooter";
import { usePaneManager } from "@features/pane-manager/store";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import { openConsole } from "@features/handoff-to-console";
import { logout } from "@features/logout";
import { generationSettingStore } from "@entities/generation-setting";
import { consoleNextForSourceTask } from "./consoleNext";

function HomeCard({
    icon,
    iconBg,
    title,
    sub,
    onClick,
    disabled,
}: {
    icon: React.ReactNode;
    iconBg: string;
    title: string;
    sub: string;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-shadow ${disabled ? "cursor-default bg-sidebar-bg opacity-60" : "cursor-pointer bg-surface shadow-[0_1px_3px_rgba(42,39,35,.06),0_4px_10px_rgba(42,39,35,.04)] hover:shadow-[0_2px_6px_rgba(42,39,35,.1),0_8px_20px_rgba(42,39,35,.06)]"}`}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
        >
            <span
                className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${iconBg}`}
            >
                {icon}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{title}</span>
                <span className="block text-xs text-text-muted">{sub}</span>
            </span>
            <ChevronRight size={16} className="shrink-0 text-text-muted" />
        </button>
    );
}

function SectionLabel({ children }: { children: string }) {
    return (
        <p className="px-4 pt-5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            {children}
        </p>
    );
}

export function HomePan() {
    const openPane = usePaneManager((s) => s.openPane);
    // ここはほぼスクロールしないが、画面ごとの当たり外れを作らないため同じ仕掛けを通す
    // （[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();
    const [consoleBusy, setConsoleBusy] = useState(false);

    function handleOpenConsole() {
        setConsoleBusy(true);
        // IM 生成 deck なら生成元タスクへ一点着地させる（document settings 埋め込みの sourceTaskId）。
        // 生成元が無ければ next 無し＝既定（/home）へ。
        const next = consoleNextForSourceTask(generationSettingStore.getSourceTaskId());
        void openConsole(next ? { next } : {}).finally(() => setConsoleBusy(false));
    }

    return (
        <>
            <PaneHeader title="ホーム" />
            <div ref={scroll.ref} className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-3">
                <SectionLabel>生成設定</SectionLabel>
                <HomeCard
                    icon={<List size={20} />}
                    iconBg="bg-interactive/15 text-interactive-active"
                    title="生成設定"
                    sub="スライドと生成担当を見渡す"
                    onClick={() => openPane("generation-setting")}
                />
                <HomeCard
                    icon={<Layers size={20} />}
                    iconBg="bg-brand-light text-brand-dark"
                    title="スライドと要素"
                    sub="編集中のスライドを設定する"
                    onClick={() => openPane("slide")}
                />

                <SectionLabel>レビュー</SectionLabel>
                <HomeCard
                    icon={<ClipboardCheck size={20} />}
                    iconBg="bg-warning/15 text-warning"
                    title="生成結果"
                    sub="根拠と要確認事項を閲覧"
                    onClick={() => openPane("results")}
                />

                <SectionLabel>Web コンソール連携</SectionLabel>
                <HomeCard
                    icon={<Upload size={20} />}
                    iconBg="bg-success/15 text-success"
                    title="フォーマット登録"
                    sub="現在のPPTXをフォーマットとして登録"
                    onClick={() => openPane("format")}
                />
                <HomeCard
                    icon={<Play size={20} />}
                    iconBg="bg-brand-light text-brand-dark"
                    title="スライド生成"
                    sub="生成タスクを作成して開始"
                    onClick={() => openPane("generate")}
                />
            </div>
            <PaneFooter>
                <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<ExternalLink size={14} />}
                    onClick={handleOpenConsole}
                    disabled={consoleBusy}
                >
                    Web コンソール
                </Button>
                <span className="flex-1" />
                <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<LogOut size={14} />}
                    onClick={() => void logout()}
                >
                    ログアウト
                </Button>
            </PaneFooter>
        </>
    );
}
